// GroupingPanel — surfaces group membership on PartyDetail.
//
// For an own_account ACCOUNT (treasury_slug != null): shows current parent
//   group + "Change group" inline picker. Operator can move the account in/out
//   of a group at any time.
//
// For an own_account GROUP (treasury_slug == null): shows list of children
//   (accounts attached to this group) with quick links to each.
//
// On screen a desk treasury account is an "account", never a "rail" (§6).
//
// Wired into PartyDetail; renders nothing for customer/counterparty types.

import { useState, useMemo } from 'react';
import { errorWords } from '../lib/errors';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users2 } from 'lucide-react';
import { api } from '../lib/api';
import { tg } from '../lib/telegram';
import { Section } from './Section';
import { EmptyState, MoreDetails } from './kit';
import { KeyValue } from './KeyValue';
import { Combobox, type ComboboxOption } from './Combobox';
import { partyName, railName } from '../lib/naming';

interface Props {
  partyType: 'customer' | 'counterparty' | 'own_account';
  slug:      string;
  isGroup:   boolean;                     // treasury_slug IS NULL
  parentSlug?:        string | null;      // current parent (accounts only)
  parentDisplayName?: string | null;
  children?: Array<{ id: number; slug: string; display_name: string; party_type: string; treasury_slug: string | null }>;
}

export function GroupingPanel({ partyType, slug, isGroup, parentSlug, parentDisplayName, children }: Props) {
  if (partyType !== 'own_account') return null;

  if (isGroup) {
    return <GroupChildren children={children ?? []} />;
  }
  return (
    <RailParent
      ownSlug={slug}
      parentSlug={parentSlug ?? null}
      parentDisplayName={parentDisplayName ?? null}
    />
  );
}

// ── Group → show children list ────────────────────────────────────────────
function GroupChildren({ children }: { children: NonNullable<Props['children']> }) {
  return (
    <Section title="Children in this group" icon={<Users2 size={14} />}>
      {children.length === 0 ? (
        <>
          <EmptyState className="!py-2">No accounts in this group yet.</EmptyState>
          {/* Sweep, 21 Oct 2026: the empty line is six words; how to add one is a tap away. */}
          <MoreDetails label="How to add one">
            <p className="text-meta text-tg-hint">
              Add one from Parties → New → Own account → Account, and pick this group as its parent.
            </p>
          </MoreDetails>
        </>
      ) : (
        <div className="space-y-1">
          {children.map((c) => (
            <Link
              key={c.id}
              to={`/parties/${encodeURIComponent(c.slug)}?entity=dubaiotc`}
              className="flex justify-between items-baseline py-1 text-body hover:bg-tg-secondary rounded px-2 -mx-2"
            >
              {/* A group's children are accounts, so they get the account
                  grammar — the same string Treasury and RailPicker show. The
                  ledger key that stood beside each one is an internal
                  identifier (§6) and is gone (20 Sept 2026). */}
              <span>{c.treasury_slug
                ? railName({ label: c.display_name, slug: c.treasury_slug })
                : partyName(c)}</span>
              <span className="text-tg-hint" aria-hidden="true">›</span>
            </Link>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Account → show parent + inline change picker ──────────────────────────
function RailParent({ ownSlug, parentSlug, parentDisplayName }: { ownSlug: string; parentSlug: string | null; parentDisplayName: string | null }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState('');

  const groupsQ = useQuery({
    queryKey: ['parties', 'groups'],
    queryFn:  () => api.listParties({ entity: 'dubaiotc', is_group: true }),
    enabled:  editing,
  });
  const options: ComboboxOption[] = useMemo(() =>
    (groupsQ.data?.parties ?? []).map((p) => ({ value: p.slug, label: partyName(p) }))
  , [groupsQ.data]);

  const save = useMutation({
    mutationFn: (newParent: string | null) =>
      api.patchParty(ownSlug, { parent_party_slug: newParent }),
    onSuccess: () => {
      tg()?.HapticFeedback.notificationOccurred('success');
      qc.invalidateQueries({ queryKey: ['party', ownSlug] });
      qc.invalidateQueries({ queryKey: ['parties'] });
      setEditing(false);
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  return (
    <Section title="Parent group" icon={<Users2 size={14} />}>
      {!editing ? (
        <>
          <KeyValue rows={[{
            label: 'Group',
            value: parentSlug
              ? <Link to={`/parties/${encodeURIComponent(parentSlug)}?entity=dubaiotc`} className="text-tg-link hover:underline">
                  {partyName({ display_name: parentDisplayName, slug: parentSlug })}
                </Link>
              : <span className="text-tg-hint">not grouped</span>,
          }]} />
          <button onClick={() => setEditing(true)} className="btn-link text-meta">
            {parentSlug ? 'Change group' : '+ Attach to a group'}
          </button>
        </>
      ) : (
        <div className="space-y-2 pt-1">
          <Combobox
            value={pick}
            onChange={setPick}
            options={options}
            placeholder={options.length === 0 ? 'No groups exist yet' : 'Pick a group'}
            loading={groupsQ.isFetching}
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              className="btn-primary text-body py-1.5"
              disabled={!pick || save.isPending}
              onClick={() => save.mutate(pick)}
            >
              {save.isPending ? 'Saving…' : 'Move to group'}
            </button>
            {parentSlug && (
              <button
                className="btn-secondary text-tg-destructive text-body py-1.5"
                disabled={save.isPending}
                onClick={() => save.mutate(null)}
              >
                Remove from group
              </button>
            )}
          </div>
          <button onClick={() => setEditing(false)} className="btn-link text-meta">cancel</button>
          {save.isError && <p className="text-tg-destructive text-meta">{errorWords(save.error)}</p>}
        </div>
      )}
    </Section>
  );
}
